import React, { useState } from 'react';
import { toast } from 'sonner';
import { Sun, LogOut, ShieldCheck, Loader2, Percent, ChevronRight, KeyRound, X, Trash2, AlertTriangle } from 'lucide-react';
import { sellerApi } from '../sellerAuth';
import { useTheme } from '../theme/ThemeContext';
import { money } from './helpers';

const THEMES = [
  { key: 'light', label: 'Light', icon: Sun },
];

const ProfileScreen = ({ seller, onSignOut, onUpdated }) => {
  const { mode, setMode } = useTheme();
  const [taxPct, setTaxPct] = useState(((seller?.tax_rate || 0) * 100).toString());
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwBusy, setPwBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [delBusy, setDelBusy] = useState(false);

  const deleteAccount = async () => {
    setDelBusy(true);
    try {
      await sellerApi.delete('/seller/account');
      toast.success('Account deleted');
      onSignOut && onSignOut();
    } catch (e) { toast.error('Could not delete account'); setDelBusy(false); }
  };

  const changePw = async () => {
    if (pw.next.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (pw.next !== pw.confirm) { toast.error('New passwords do not match'); return; }
    setPwBusy(true);
    try {
      await sellerApi.post('/seller/change-password', { current_password: pw.current, new_password: pw.next });
      toast.success('Password changed');
      setShowPw(false); setPw({ current: '', next: '', confirm: '' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not change password'); } finally { setPwBusy(false); }
  };

  const saveTax = async () => {
    const rate = (parseFloat(taxPct) || 0) / 100;
    setBusy(true);
    try {
      const res = await sellerApi.put('/seller/settings', { tax_rate: rate });
      toast.success('Tax rate saved');
      onUpdated && onUpdated(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); } finally { setBusy(false); }
  };

  return (
    <div className="px-5 pt-6">
      <h1 className="font-heading text-3xl font-black tracking-tight">Profile</h1>

      {/* Account card */}
      <div className="mt-4 rounded-3xl bg-card border border-border p-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center text-primary font-heading font-black text-lg">{(seller?.name || '?')[0]?.toUpperCase()}</div>
          <div className="min-w-0">
            <p className="font-heading font-bold truncate">{seller?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{seller?.email}</p>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Balance</span>
          <span className="font-heading font-bold">{money(seller?.balance)}</span>
        </div>
        <div className="mt-2 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Payments</span>
          <span className={`flex items-center gap-1.5 font-bold ${seller?.card_payments_active ? 'text-primary' : 'text-muted-foreground'}`}><ShieldCheck size={15} strokeWidth={2.5} /> {seller?.card_payments_active ? 'Active' : 'Pending'}</span>
        </div>
      </div>

      {/* Tax config */}
      <div className="mt-3 rounded-3xl bg-card border border-border p-5">
        <div className="flex items-center gap-2 mb-1"><Percent size={16} strokeWidth={2.5} className="text-primary" /><p className="font-heading font-bold">Sales tax rate</p></div>
        <p className="text-xs text-muted-foreground mb-3">Applied automatically on each sale. Default 0%.</p>
        <div className="flex gap-2">
          <div className="flex-1 flex items-center rounded-2xl bg-secondary px-4">
            <input data-testid="tax-rate-input" type="number" min="0" max="50" step="0.1" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} className="flex-1 bg-transparent py-3 text-sm outline-none" />
            <span className="text-muted-foreground text-sm">%</span>
          </div>
          <button data-testid="save-tax-button" disabled={busy} onClick={saveTax} className="px-6 rounded-2xl bg-primary text-primary-foreground font-bold text-sm active:scale-95 transition-transform disabled:opacity-50">{busy ? <Loader2 size={16} className="animate-spin" /> : 'Save'}</button>
        </div>
      </div>

      {/* Theme */}
      <div className="mt-3 rounded-3xl bg-card border border-border p-5">
        <p className="font-heading font-bold mb-3">Appearance</p>
        <div className="flex gap-1 bg-secondary rounded-full p-1">
          {THEMES.map((t) => {
            const Icon = t.icon;
            const on = mode === t.key;
            return (
              <button key={t.key} data-testid={`theme-${t.key}`} onClick={() => setMode(t.key)} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full text-sm font-bold transition-colors ${on ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}>
                <Icon size={16} strokeWidth={2.5} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stripe */}
      <div className="mt-3 rounded-3xl bg-card border border-border overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4">
          <span className="text-sm font-bold">Stripe account</span>
          <span className="text-xs text-muted-foreground truncate max-w-[45%]">{seller?.stripe_account_id}</span>
        </div>
      </div>

      {/* Security */}
      <button data-testid="change-password-open" onClick={() => setShowPw(true)} className="mt-3 w-full rounded-3xl bg-card border border-border px-5 py-4 flex items-center justify-between active:scale-[0.99] transition-transform">
        <span className="flex items-center gap-2 text-sm font-bold"><KeyRound size={16} strokeWidth={2.5} className="text-primary" /> Change password</span>
        <ChevronRight size={18} className="text-muted-foreground" />
      </button>

      <button data-testid="signout-button" onClick={onSignOut} className="mt-4 mb-2 w-full h-13 py-3.5 rounded-full bg-destructive/10 text-destructive font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
        <LogOut size={18} strokeWidth={2.5} /> Sign out
      </button>
      <button data-testid="delete-account-open" onClick={() => setShowDelete(true)} className="w-full py-3 text-destructive/80 font-bold text-sm flex items-center justify-center gap-2">
        <Trash2 size={16} strokeWidth={2.5} /> Delete my account
      </button>
      <p className="text-center text-[11px] text-muted-foreground mt-2">flix. · Each seller is paid directly · 1.49% platform fee</p>

      {showPw && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowPw(false)} />
          <div className="relative w-full max-w-md bg-background border-t border-border rounded-t-3xl p-6 pb-10 rise-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-black">Change password</h3>
              <button onClick={() => setShowPw(false)} className="text-muted-foreground"><X size={22} /></button>
            </div>
            <div className="space-y-2">
              <input data-testid="current-password-input" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} placeholder="Current password" className="w-full rounded-2xl bg-card border border-border px-4 py-3 text-sm outline-none focus:border-primary transition" />
              <input data-testid="new-password-input" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} placeholder="New password (min 6 characters)" className="w-full rounded-2xl bg-card border border-border px-4 py-3 text-sm outline-none focus:border-primary transition" />
              <input data-testid="confirm-password-input" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} placeholder="Confirm new password" className="w-full rounded-2xl bg-card border border-border px-4 py-3 text-sm outline-none focus:border-primary transition" />
            </div>
            <button data-testid="change-password-submit" disabled={pwBusy} onClick={changePw} className="mt-4 w-full h-12 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center active:scale-[0.98] transition-transform disabled:opacity-50">
              {pwBusy ? <Loader2 size={18} className="animate-spin" /> : 'Update password'}
            </button>
          </div>
        </div>
      )}

      {showDelete && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => !delBusy && setShowDelete(false)} />
          <div className="relative w-full max-w-md bg-background border-t border-border rounded-t-3xl p-6 pb-10 rise-in">
            <div className="w-12 h-12 rounded-2xl bg-destructive/12 flex items-center justify-center mb-4"><AlertTriangle size={22} className="text-destructive" /></div>
            <h3 className="font-heading text-xl font-black">Delete your account?</h3>
            <p className="text-sm text-muted-foreground mt-2">This permanently removes your FLIX account, customers and transaction history. This cannot be undone.</p>
            {seller?.balance > 0 && (
              <div className="mt-3 flex items-start gap-2 rounded-2xl bg-orange-400/10 border border-orange-400/25 p-3 text-xs text-orange-500">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                <span>You have an unwithdrawn balance of <b>{money(seller.balance)}</b>. Make sure your Stripe payout has cleared first — your Stripe account itself stays in Stripe.</span>
              </div>
            )}
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowDelete(false)} disabled={delBusy} className="flex-1 h-12 rounded-full bg-secondary text-foreground font-bold">Cancel</button>
              <button data-testid="delete-account-confirm" onClick={deleteAccount} disabled={delBusy} className="flex-1 h-12 rounded-full bg-destructive text-destructive-foreground font-bold flex items-center justify-center disabled:opacity-50">
                {delBusy ? <Loader2 size={18} className="animate-spin" /> : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileScreen;
